import { Box, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAgent from '../hooks/useAgentSubscription';
import { useLoadMessages } from '../hooks/useLoadMessages';
import { useAiChatStore } from '../store/aiChat.store';
import AiChatAssistantMessage from './AiChatAssistantMessage';
import AiChatHeader from './AiChatHeader';
import AiChatStatus from './AiChatStatus';
import AiChatToolCalls from './AiChatToolCalls';
import AiChatUserMessage from './AiChatUserMessage';

export default function AiChatConversationThread() {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  const conversationId = useAiChatStore((state) => state.conversationId);
  const messages = useAiChatStore((state) => state.messages);
  const streamingMessage = useAiChatStore((state) => state.streamingMessage);
  const prependMessages = useAiChatStore((state) => state.prependMessages);
  const messageEndCursor = useAiChatStore((state) => state.messageEndCursor);
  const hasMoreMessages = useAiChatStore((state) => state.hasMoreMessages);
  const setMessagePagination = useAiChatStore((state) => state.setMessagePagination);
  const setWaitingForResponse = useAiChatStore((state) => state.setWaitingForResponse);

  const { loadMessages } = useLoadMessages();

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const node = scrollContainerRef.current;
    if (!node) {
      return;
    }

    node.scrollTo({ top: node.scrollHeight, behavior });
  }, []);

  useEffect(() => {
    if (conversationId) {
      scrollToBottom('instant');
    }
  }, [conversationId, scrollToBottom]);

  // Scroll when streaming message updates
  useEffect(() => {
    if (streamingMessage) {
      scrollToBottom('smooth');
    }
  }, [streamingMessage, scrollToBottom]);

  // Load older messages when scrolling up
  const loadOlderMessages = useCallback(async () => {
    if (!conversationId || !hasMoreMessages || loadingOlderMessages) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    // Save current scroll position and height
    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;

    setLoadingOlderMessages(true);

    try {
      const result = await loadMessages({ conversationId, first: 20, after: messageEndCursor, sortDirection: 'desc' });

      // Reverse to get ascending order (oldest first)
      const olderMessages = result.messages.reverse();

      if (olderMessages.length > 0) {
        // Prepend older messages to the beginning
        prependMessages(olderMessages);
        setMessagePagination(result.pageInfo.endCursor, result.pageInfo.hasNextPage);

        // Restore scroll position after messages are rendered
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            const scrollHeightDiff = newScrollHeight - previousScrollHeight;
            container.scrollTop = previousScrollTop + scrollHeightDiff;
          }
        }, 0);
      } else {
        setMessagePagination(null, false);
      }
    } catch (error) {
      console.error('Error loading older messages:', error);
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [
    conversationId,
    hasMoreMessages,
    loadingOlderMessages,
    messageEndCursor,
    loadMessages,
    prependMessages,
    setMessagePagination,
  ]);

  // Handle scroll event to load older messages
  const handleMessageScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop } = container;
    const isNearTop = scrollTop < 100;

    if (isNearTop && hasMoreMessages && !loadingOlderMessages) {
      loadOlderMessages();
    }
  }, [hasMoreMessages, loadingOlderMessages, loadOlderMessages]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleMessageScroll);
    return () => container.removeEventListener('scroll', handleMessageScroll);
  }, [handleMessageScroll]);

  const agentProps = useMemo(
    () => ({
      onReply: () => {
        setWaitingForResponse(false);
        scrollToBottom();
      },
    }),
    [scrollToBottom, setWaitingForResponse]
  );

  useAgent(agentProps);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
      <AiChatHeader />
      {/* Scrollable messages area */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          px: '24px',
          pt: 2,
          minHeight: 0,
          position: 'relative',
          gap: 2,
          flexGrow: 1,
          overflowY: 'auto',
        }}
      >
        {loadingOlderMessages && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        <AiChatStatus />
        {messages.map((msg, index) => {
          if (msg.role === 'USER') {
            return <AiChatUserMessage key={index} content={msg.content} documents={msg.documents} />;
          } else {
            return <AiChatAssistantMessage key={index} content={msg.content} />;
          }
        })}
        {streamingMessage && <AiChatAssistantMessage content={streamingMessage} isStreaming />}
      </Box>
      {/* Status indicators pinned above input */}
      <Box sx={{ px: '24px', pb: 1, flexShrink: 0 }}>
        <AiChatToolCalls />
      </Box>
    </Box>
  );
}
